import { useEffect, useRef } from 'react';
import { useOperatorPanelStore } from './operator-panel-store';
import { shouldCloseForNewOperatorPanelRequest } from './operator-panel-close-gate';

/**
 * FRONT-1991: closes a full-screen modal the instant a NEW desktop-operator-
 * panel request appears — the "modal viewers are mutually exclusive, never
 * stacked" rule (CLAUDE.md). The operator panel is a second full-screen
 * overlay mounted separately in `app-layout.tsx` and has no way to close a
 * modal sitting underneath it itself, so every such modal watches this same
 * request store and closes itself instead.
 *
 * Aikido finding, FRONT-1991: this effect was duplicated verbatim in
 * `inbox-chat-modal.tsx` and `coding-session-modal.tsx` (including the
 * mount-time-seed detail below, review F14). This hook is the one copy both
 * now call.
 *
 * `onClose` is read through a ref, so callers never need an
 * exhaustive-deps suppression: the effect fires ONLY when `operatorPanelRequest`
 * changes, never merely because the caller re-rendered with a new `onClose`
 * identity.
 *
 * Seeded with the MOUNT-TIME request value, not `null` — see
 * `shouldCloseForNewOperatorPanelRequest`'s own doc comment (review F14/R10):
 * a modal opened while a request already happens to be set reads that as
 * "already set", never as a fresh one appearing, while a LATER, different
 * request arriving still closes it.
 */
export function useCloseOnOperatorPanelRequest(onClose: () => void): void {
  const operatorPanelRequest = useOperatorPanelStore((s) => s.request);
  const prevOperatorPanelRequestRef = useRef(operatorPanelRequest);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (shouldCloseForNewOperatorPanelRequest(prevOperatorPanelRequestRef.current, operatorPanelRequest)) {
      onCloseRef.current();
    }
    prevOperatorPanelRequestRef.current = operatorPanelRequest;
  }, [operatorPanelRequest]);
}
